'use client';

import * as React from 'react';
import * as Popover from '@radix-ui/react-popover';
import posthog from 'posthog-js';
import { useCompleteTour } from '@/api/queries/tours.queries';
import { TourCard } from '@/features/tours/primitives/TourCard';
import { TourSpotlight } from '@/features/tours/primitives/TourSpotlight';
import '@/features/tours/tokens';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import {
  endTour,
  markTourSeen,
  useActiveTour,
  type ActiveTour,
} from '@/shared/lib/tours/tour-store';
import { useAnchorTarget } from './use-anchor-target';

/** Spotlight padding (6px) + breathing room, so the arrow tip clears the ring. */
const CARD_GAP = 16;
/** Keep the card this far from every viewport edge when it shifts or flips. */
const EDGE_PADDING = 16;

/**
 * Renders the active feature tour (if any): spotlight on the step's anchor and
 * a card anchored next to it (below, flipping above / shifting sideways when
 * there is no room — Radix Popper). Steps without an on-screen anchor get a
 * free-standing card bottom-centre. Generic — it never knows which feature a
 * tour belongs to. Start with `?tour=<id>` (TourLinkWatcher), a first visit to
 * an `autoStart` tour's `path` (TourAutoStarter) or `startTour(id)`.
 */
export function TourHost() {
  const active = useActiveTour();
  if (!active) return null;
  // Keyed so a new tour always starts at step 0 with fresh analytics state.
  return <TourRunner key={active.tour.id} active={active} />;
}

function TourRunner({ active }: { active: ActiveTour }) {
  const { tour, trigger } = active;
  const [stepIndex, setStepIndex] = React.useState(0);
  const step = tour.steps[stepIndex] ?? tour.steps[0];
  const total = tour.steps.length;
  const target = useAnchorTarget(step.anchor);
  const anchored = target !== null;
  const titleId = React.useId();
  const cardRef = React.useRef<HTMLDivElement>(null);
  const { mutate: completeOnServer } = useCompleteTour();

  React.useEffect(() => {
    posthog.capture(AnalyticsEvent.TOUR_STARTED, {
      tour_id: tour.id,
      tour_version: tour.version,
      total_steps: total,
      trigger,
    });
  }, [tour.id, tour.version, total, trigger]);

  React.useEffect(() => {
    posthog.capture(AnalyticsEvent.TOUR_STEP_VIEWED, {
      tour_id: tour.id,
      step_index: stepIndex,
      step_id: step.id,
      total_steps: total,
    });
    cardRef.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus({ preventScroll: true });
    // `anchored`: the card remounts when the target appears — refocus it.
  }, [anchored, step.id, stepIndex, total, tour.id]);

  const finish = React.useCallback(
    (outcome: 'completed' | 'skipped') => {
      posthog.capture(
        outcome === 'completed' ? AnalyticsEvent.TOUR_COMPLETED : AnalyticsEvent.TOUR_SKIPPED,
        { tour_id: tour.id, step_index: stepIndex, step_id: step.id, total_steps: total }
      );
      markTourSeen(tour);
      // Skip counts as done on the BE too — it only stores "don't auto-start again".
      completeOnServer(tour.slug);
      endTour();
    },
    [completeOnServer, step.id, stepIndex, total, tour]
  );

  const next = React.useCallback(() => setStepIndex((i) => Math.min(i + 1, total - 1)), [total]);
  const back = React.useCallback(() => setStepIndex((i) => Math.max(i - 1, 0)), []);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish('skipped');
      else if (e.key === 'ArrowRight') {
        if (stepIndex >= total - 1) finish('completed');
        else next();
      } else if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [back, finish, next, stepIndex, total]);

  const card = (
    <TourCard
      stepIndex={stepIndex}
      total={total}
      title={step.title}
      body={step.body}
      titleId={titleId}
      onNext={next}
      onBack={back}
      onDone={() => finish('completed')}
      onSkip={() => finish('skipped')}
    />
  );

  return (
    <>
      <div className="ttr-layer">
        <TourSpotlight
          rect={target?.rect ?? null}
          // Client-only render (no active tour on the server) — window is safe.
          viewport={{ width: window.innerWidth, height: window.innerHeight }}
        />
        {!target && (
          <div ref={cardRef} className="ttr-card-slot" role="dialog" aria-labelledby={titleId}>
            {card}
          </div>
        )}
      </div>
      {target && (
        <AnchoredCard element={target.element} cardRef={cardRef} titleId={titleId}>
          {card}
        </AnchoredCard>
      )}
    </>
  );
}

/**
 * Card pinned to the step's element through a Radix Popover virtual anchor.
 * Always open and non-modal: outside clicks and Escape are owned by the
 * runner (Skip / Done / Esc), never by the popover.
 */
function AnchoredCard({
  element,
  cardRef,
  titleId,
  children,
}: {
  element: Element;
  cardRef: React.RefObject<HTMLDivElement | null>;
  titleId: string;
  children: React.ReactNode;
}) {
  const anchorRef = React.useMemo(
    () => ({ current: { getBoundingClientRect: () => element.getBoundingClientRect() } }),
    [element]
  );
  const keep = (e: Event) => e.preventDefault();
  return (
    <Popover.Root open modal={false}>
      <Popover.Anchor virtualRef={anchorRef} />
      <Popover.Portal>
        <Popover.Content
          ref={cardRef}
          className="ttr-pop"
          side="bottom"
          align="center"
          sideOffset={CARD_GAP}
          collisionPadding={EDGE_PADDING}
          role="dialog"
          aria-labelledby={titleId}
          onOpenAutoFocus={keep}
          onCloseAutoFocus={keep}
          onEscapeKeyDown={keep}
          onInteractOutside={keep}
        >
          {children}
          <Popover.Arrow className="ttr-arrow" width={16} height={8} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
